import { useMemo, useState } from 'react';
import { Pencil, Plus, Trash2, Users } from 'lucide-react';
import { LiksButton } from '../../components/liks/LiksButton/LiksButton';
import { LiksSearch } from '../../components/liks/LiksSearch/LiksSearch';
import {
  useAssignTenantUserLabelMutation,
  useCreateTenantLabelMutation,
  useDeleteTenantLabelMutation,
  useRenameTenantLabelMutation,
  useUnassignTenantUserLabelMutation,
} from '../../features/tenant/mutations/useTenantLabelMutations';
import {
  useTenantLabelUsersQueries,
  useTenantLabelUsersQuery,
} from '../../features/tenant/queries/useTenantLabelUsersQuery';
import { useTenantLabelsQuery } from '../../features/tenant/queries/useTenantLabelsQuery';
import { useTenantUsersQuery } from '../../features/tenant/queries/useTenantUsersQuery';
import { useI18n } from '../../i18n/useI18n';
import type { WithResourceId } from '../../types/knowledge';
import type { UserLabel } from '../../types/tenant';
import type { TenantAssignableUser } from './TenantResourceUserAssignments';
import { TenantLabelsSectionDialogs } from './TenantLabelsSectionDialogs';
import './TenantLabelsGroupsSection.css';

type NamedLabel = WithResourceId<UserLabel> & { name: string };

export function TenantLabelsSection() {
  const { translate } = useI18n();
  const labelsQuery = useTenantLabelsQuery();
  const usersQuery = useTenantUsersQuery();
  const createMutation = useCreateTenantLabelMutation();
  const renameMutation = useRenameTenantLabelMutation();
  const deleteMutation = useDeleteTenantLabelMutation();
  const assignMutation = useAssignTenantUserLabelMutation();
  const unassignMutation = useUnassignTenantUserLabelMutation();

  const [search, setSearch] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState<NamedLabel | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<NamedLabel | null>(null);
  const [manageTarget, setManageTarget] = useState<NamedLabel | null>(null);
  const [pendingUserId, setPendingUserId] = useState<string | null>(null);

  const directoryUsers = useMemo(
    () =>
      (usersQuery.data ?? []).filter(
        (user): user is TenantAssignableUser =>
          typeof user.id === 'string' && user.id.length > 0,
      ),
    [usersQuery.data],
  );

  const labels = useMemo(() => {
    const query = search.trim().toLowerCase();
    const list = [...(labelsQuery.data ?? [])].sort((a, b) =>
      a.name.localeCompare(b.name),
    );
    if (query === '') {
      return list;
    }
    return list.filter((label) => label.name.toLowerCase().includes(query));
  }, [labelsQuery.data, search]);

  const labelIds = useMemo(() => labels.map((label) => label.id), [labels]);
  const labelUsersQueries = useTenantLabelUsersQueries(
    labelIds,
    !labelsQuery.isLoading && !labelsQuery.isError,
  );

  const userCountByLabelId = useMemo(() => {
    const map = new Map<string, number>();
    for (let index = 0; index < labelIds.length; index += 1) {
      const labelId = labelIds[index];
      const users = labelUsersQueries[index]?.data;
      if (labelId && users) {
        map.set(labelId, users.length);
      }
    }
    return map;
  }, [labelIds, labelUsersQueries]);

  const manageUsersQuery = useTenantLabelUsersQuery(manageTarget?.id);

  const formatUserCount = (count: number | undefined) => {
    if (count == null) {
      return translate(
        'organisationManagement.labelsAndGroups.userCountLoading',
      );
    }
    return translate('organisationManagement.labelsAndGroups.userCount', {
      count: String(count),
    });
  };

  return (
    <section className="tenant-labels-groups__section">
      <div className="tenant-labels-groups__section-header">
        <div>
          <h2 className="tenant-labels-groups__section-title">
            {translate('organisationManagement.labelsAndGroups.labelsTitle')}
          </h2>
          <p className="tenant-labels-groups__section-description">
            {translate(
              'organisationManagement.labelsAndGroups.labelsDescription',
            )}
          </p>
        </div>
        <LiksButton type="button" onClick={() => setIsCreateOpen(true)}>
          <Plus strokeWidth={2} aria-hidden />
          {translate('organisationManagement.labelsAndGroups.createLabel')}
        </LiksButton>
      </div>

      <div className="tenant-labels-groups__toolbar">
        <LiksSearch
          className="tenant-labels-groups__search"
          value={search}
          onChange={setSearch}
          placeholder={translate(
            'organisationManagement.labelsAndGroups.searchLabelsPlaceholder',
          )}
          aria-label={translate(
            'organisationManagement.labelsAndGroups.searchLabelsLabel',
          )}
          clearLabel={translate('common.search.clear')}
          variant="on-light"
        />
      </div>

      {labelsQuery.isLoading ? (
        <p className="tenant-labels-groups__status">
          {translate('organisationManagement.labelsAndGroups.labelsLoading')}
        </p>
      ) : null}

      {labelsQuery.isError ? (
        <p className="tenant-labels-groups__status" role="alert">
          {translate('organisationManagement.labelsAndGroups.labelsLoadError')}
        </p>
      ) : null}

      {!labelsQuery.isLoading && !labelsQuery.isError && labels.length === 0 ? (
        <p className="tenant-labels-groups__empty">
          {search.trim()
            ? translate(
                'organisationManagement.labelsAndGroups.labelsNoResults',
              )
            : translate('organisationManagement.labelsAndGroups.labelsEmpty')}
        </p>
      ) : null}

      {labels.length > 0 ? (
        <ul className="tenant-labels-groups__list">
          {labels.map((label) => (
            <li key={label.id} className="tenant-labels-groups__item">
              <div className="tenant-labels-groups__item-row">
                <div className="tenant-labels-groups__item-main">
                  <span className="tenant-labels-groups__chip">
                    {label.name}
                  </span>
                  <span className="tenant-labels-groups__user-count">
                    {formatUserCount(userCountByLabelId.get(label.id))}
                  </span>
                </div>
                <div className="tenant-labels-groups__item-actions">
                  <LiksButton
                    type="button"
                    variant="secondary"
                    onClick={() => setManageTarget(label)}
                    aria-label={translate(
                      'organisationManagement.labelsAndGroups.manageUsersFor',
                      { name: label.name },
                    )}
                  >
                    <Users strokeWidth={2} aria-hidden />
                    {translate(
                      'organisationManagement.labelsAndGroups.manageUsers',
                    )}
                  </LiksButton>
                  <LiksButton
                    type="button"
                    variant="secondary"
                    onClick={() => setRenameTarget(label)}
                    aria-label={translate(
                      'organisationManagement.labelsAndGroups.renameLabel',
                      { name: label.name },
                    )}
                  >
                    <Pencil strokeWidth={2} aria-hidden />
                    {translate('organisationManagement.labelsAndGroups.rename')}
                  </LiksButton>
                  <LiksButton
                    type="button"
                    variant="danger"
                    onClick={() => setDeleteTarget(label)}
                    aria-label={translate(
                      'organisationManagement.labelsAndGroups.deleteLabel',
                      { name: label.name },
                    )}
                  >
                    <Trash2 strokeWidth={2} aria-hidden />
                    {translate('organisationManagement.labelsAndGroups.delete')}
                  </LiksButton>
                </div>
              </div>
            </li>
          ))}
        </ul>
      ) : null}

      <TenantLabelsSectionDialogs
        isCreateOpen={isCreateOpen}
        onCloseCreate={() => setIsCreateOpen(false)}
        renameTarget={renameTarget}
        onCloseRename={() => setRenameTarget(null)}
        deleteTarget={deleteTarget}
        onCloseDelete={() => setDeleteTarget(null)}
        manageTarget={manageTarget}
        onCloseManage={() => setManageTarget(null)}
        directoryUsers={directoryUsers}
        assignedUsers={manageUsersQuery.data ?? []}
        isManageLoading={manageUsersQuery.isLoading}
        isManageError={manageUsersQuery.isError}
        pendingUserId={pendingUserId}
        setPendingUserId={setPendingUserId}
        createMutation={createMutation}
        renameMutation={renameMutation}
        deleteMutation={deleteMutation}
        assignMutation={assignMutation}
        unassignMutation={unassignMutation}
      />
    </section>
  );
}
